import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
// Import the CSS module
import styles from './Header.module.css';

// Simple throttle function
function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
}

function GuestHeader() {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const collapseThreshold = 50; // Pixels scrolled down before collapsing
    // const expandThreshold = 10;   // Pixels scrolled up before expanding - REMOVED

    let lastKnownScrollPosition = 0;

    const handleScroll = throttle(() => {
      const currentScrollY = window.scrollY;
      // Determine scroll direction. Add a small buffer (e.g., 5px) to avoid jitter near the top.
      const isScrollingDown = currentScrollY > lastKnownScrollPosition && currentScrollY > 5;
      const isScrollingUp = currentScrollY < lastKnownScrollPosition;
      lastKnownScrollPosition = currentScrollY <= 0 ? 0 : currentScrollY; // Don't go below 0

       // Apply scroll logic using functional update
       setIsScrolled(currentIsScrolled => {
         if (isScrollingDown && currentScrollY > collapseThreshold) {
           // Scrolling down past threshold -> Collapse
           return true;
         } else if (isScrollingUp && currentIsScrolled) {
           // Scrolling up and currently collapsed -> Expand
           return false;
         }
         // Otherwise, maintain the current state (e.g., scrolling down but not past threshold, or already expanded)
         return currentIsScrolled;
       });

    }, 100); // Throttle check to every 100ms

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check in case the page loads already scrolled
    handleScroll();

    // Clean up the event listener when the component unmounts
    return () => window.removeEventListener('scroll', handleScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // isScrolled dependency removed to use functional updates


  // Determine if the header should be collapsed
  const isCollapsed = isScrolled && !isHovering;

  return (
    // Apply base header style + conditional collapsed class.
    // Add mouse enter/leave handlers
    <header 
      className={`${styles.headerBase} ${isCollapsed ? styles.scrolled : ''}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Use container for padding and centering - applied within headerBase now */}
      {/* Main flex container for logo and nav links */}
      <div className="flex justify-between items-center w-full h-full">
        {/* Left side: Logo/Brand Name - Apply brandLink style */}
        <div className={`${styles.brandLink}`}> {/* Apply brandLink class */} 
          <Link to="/" className="text-xl font-bold text-gray-800 hover:text-blue-600 transition-colors">
            ShopEase
          </Link>
        </div>

        {/* Right side: Navigation Links & Buttons - Wrap in navLinksContainer */}
        <div className={styles.navLinksContainer}> {/* Wrap links */} 
          <Link
            to="/login"
            // Restore original classes
            className={`${styles.navLink} text-sm font-medium ${
              location.pathname === "/login"
                ? `${styles.navLinkActive} text-blue-600` // Active state class + Tailwind color
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Login
          </Link>
          <Link
            to="/register"
            // Combine custom navLink class with Tailwind
            className={`${styles.navLink} text-sm font-medium ${
              location.pathname === "/register"
                ? `${styles.navLinkActive} text-blue-600` // Active state class + Tailwind color
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Register
          </Link>
          {/* Styled Download button as a Link */}
          <Link
            to="/download"
            // Combine custom button class with Tailwind
            className={`${styles.headerButton} flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full duration-200 ease-in-out ${
              location.pathname === "/download"
                ? "bg-blue-600 text-white shadow-md hover:bg-blue-700"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900"
            }`}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Download
          </Link>
        </div>
      </div>
    </header>
  );
}

export default GuestHeader;
